import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";

import { showErrorToast } from "../../../components/Toast";

const MIN_QUERY_LENGTH = 2;
const SUGGESTION_DEBOUNCE_MS = 300;
const DETAILS_TIMEOUT_MS = 8000;
const BIAS_RADIUS_METERS = 50000;

const DETAILS_FIELDS = ["geometry.location", "formatted_address", "name"];

const withTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("places-timeout")), DETAILS_TIMEOUT_MS)
    ),
  ]);

const newSessionToken = () => {
  const places = window.google?.maps?.places;

  return places?.AutocompleteSessionToken
    ? new places.AutocompleteSessionToken()
    : null;
};

const HomeSearchBar = ({
  address,
  coordinates,
  activeFilters,
  onSelectPlace,
  onOpenFilters,
  mapsReady,
  disabled,
}) => {
  const [input, setInput] = useState(address || "");
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [session, setSession] = useState(null);

  const editingRef = useRef(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const requestOptions = useMemo(() => {
    const options = {};

    if (session) options.sessionToken = session;

    if (mapsReady && coordinates && window.google?.maps?.LatLng) {
      options.location = new window.google.maps.LatLng(
        coordinates.latitude,
        coordinates.longitude
      );
      options.radius = BIAS_RADIUS_METERS;
    }

    return options;
  }, [session, mapsReady, coordinates]);

  const {
    ready,
    suggestions: { data, status, loading },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions,
    debounce: SUGGESTION_DEBOUNCE_MS,
    cache: false,
    initOnMount: false,
  });

  useEffect(() => {
    if (!mapsReady || ready) return;

    init();
  }, [mapsReady, ready, init]);

  useEffect(() => {
    if (!ready) return;

    setSession((current) => current || newSessionToken());

    if (editingRef.current && inputRef.current?.value.trim().length >= MIN_QUERY_LENGTH) {
      setValue(inputRef.current.value);
    }
  }, [ready, setValue]);

  useEffect(() => {
    if (editingRef.current) return;

    setInput(address || "");
  }, [address]);

  useEffect(() => {
    if (status === "REQUEST_DENIED") {
      showErrorToast("Location search is unavailable right now.");
    }
  }, [status]);

  const close = useCallback(
    ({ restore } = {}) => {
      editingRef.current = false;
      setOpen(false);
      setHighlight(-1);
      clearSuggestions();

      if (restore) setInput(address || "");
    },
    [address, clearSuggestions]
  );

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (containerRef.current?.contains(event.target)) return;

      close({ restore: true });
    };

    document.addEventListener("mousedown", onDocumentClick);

    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [close]);

  const onChange = (event) => {
    const next = event.target.value;

    editingRef.current = true;
    setInput(next);
    setOpen(true);
    setHighlight(-1);

    if (next.trim().length < MIN_QUERY_LENGTH) {
      setValue(next, false);
      clearSuggestions();
      return;
    }

    setValue(next);
  };

  const onClear = () => {
    editingRef.current = true;
    setInput("");
    setValue("", false);
    clearSuggestions();
    setHighlight(-1);
    setOpen(true);
    inputRef.current?.focus();
  };

  const onPick = async (suggestion) => {
    editingRef.current = false;
    setInput(suggestion.description);
    setOpen(false);
    setHighlight(-1);
    clearSuggestions();
    setResolving(true);

    try {
      const details = await withTimeout(
        getDetails({
          placeId: suggestion.place_id,
          fields: DETAILS_FIELDS,
          ...(session && { sessionToken: session }),
        })
      );

      const location = details?.geometry?.location;

      if (!location) throw new Error("places-no-geometry");

      onSelectPlace(
        {
          latitude: typeof location.lat === "function" ? location.lat() : location.lat,
          longitude: typeof location.lng === "function" ? location.lng() : location.lng,
        },
        details.formatted_address || suggestion.description
      );

      setSession(newSessionToken());
    } catch (error) {
      setInput(address || "");

      showErrorToast(
        error?.message === "places-timeout"
          ? "Location search timed out. Check your connection."
          : "Could not load that location. Please try again."
      );
    } finally {
      setResolving(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      close({ restore: true });
      inputRef.current?.blur();
      return;
    }

    if (!data.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => (current + 1) % data.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => (current <= 0 ? data.length - 1 : current - 1));
      return;
    }

    if (event.key === "Enter" && open) {
      event.preventDefault();
      onPick(data[highlight >= 0 ? highlight : 0]);
    }
  };

  const query = input.trim();
  const searching = loading && query.length >= MIN_QUERY_LENGTH;
  const showList = open && query.length >= MIN_QUERY_LENGTH;
  const noMatches = showList && !searching && !data.length && status === "ZERO_RESULTS";

  return (
    <div className="home-search" ref={containerRef}>
      <div className="home-search-bar">
        <svg className="home-search-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <circle cx="9" cy="9" r="5.75" />
          <path d="m13.25 13.25 3.5 3.5" />
        </svg>

        <input
          ref={inputRef}
          type="text"
          className="home-search-input"
          value={input}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          placeholder="Search an address"
          autoComplete="off"
          role="combobox"
          aria-label="Search for an address"
          aria-autocomplete="list"
          aria-controls="home-search-suggestions"
          aria-expanded={showList && (data.length > 0 || noMatches)}
          aria-activedescendant={
            highlight >= 0 ? `home-search-option-${highlight}` : undefined
          }
          disabled={disabled}
        />

        {resolving || searching ? (
          <span className="home-search-spinner" aria-hidden="true" />
        ) : (
          input && (
            <button
              type="button"
              className="home-search-clear"
              onClick={onClear}
              aria-label="Clear search"
            >
              <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false">
                <path
                  d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )
        )}

        <span className="home-search-divider" aria-hidden="true" />

        <button
          type="button"
          className={`home-search-filter${activeFilters > 0 ? " is-active" : ""}`}
          onClick={onOpenFilters}
          aria-label={
            activeFilters > 0
              ? `Filters, ${activeFilters} active`
              : "Filters"
          }
        >
          <img src="/assets/icons/filter.svg" alt="" aria-hidden="true" />
          <span className="home-search-filter-label">Filters</span>
          {activeFilters > 0 && (
            <span className="home-search-count">{activeFilters}</span>
          )}
        </button>
      </div>

      {showList && (data.length > 0 || noMatches) && (
        <ul
          className="home-search-list"
          id="home-search-suggestions"
          role="listbox"
        >
          {noMatches ? (
            <li className="home-search-empty">No places match “{query}”.</li>
          ) : (
            data.map((suggestion, index) => (
              <li key={suggestion.place_id}>
                <button
                  type="button"
                  id={`home-search-option-${index}`}
                  role="option"
                  aria-selected={index === highlight}
                  className={`home-search-option${
                    index === highlight ? " is-active" : ""
                  }`}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => onPick(suggestion)}
                >
                  <strong>
                    {suggestion.structured_formatting?.main_text ||
                      suggestion.description}
                  </strong>
                  <span>{suggestion.structured_formatting?.secondary_text}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default React.memo(HomeSearchBar);
